// solidityBaseSyntax.json
const SOLIDITY_BASE_SYNTAX = {
  title: "Solidity — Sintaxis Base",
  sections: [
    {
      id: "Base",
      heading: "Tu primer archivo en Solidity",
      text: `Antes de hablar de tipos, necesitamos entender la estructura mínima de un archivo en Solidity:
1) Indicar la versión del compilador con 'pragma'.
2) Importar archivos/librerías que vas a usar.
3) Declarar tu contrato y poner el código DENTRO de sus llaves.
Todo lo que agregues (variables, funciones, modificadores, eventos) va dentro del contrato.`
    },

    {
      id: "pragma",
      heading: "1) pragma solidity — ¿qué es y para qué sirve?",
      text: `La directiva 'pragma solidity' declara con qué versión del compilador es compatible tu código.
No “carga” Solidity ni instala el compilador: solo establece compatibilidad. 
En proyectos reales conviene usar versiones modernas (0.8.x) y fijarlas en tu toolchain (Hardhat/Foundry).`,
      codes: [
`// import solidity (como en tu ejemplo)
pragma solidity ^0.4.0;`,
`// recomendación moderna (mejor práctica hoy)
pragma solidity >=0.8.20 <0.9.0;`
      ],
      note: `0.4.x es una versión muy antigua. Mantén tu código y toolchain en 0.8.x salvo que estés estudiando código legado.`
    },

    {
      id: "imports",
      heading: "2) Importar archivos — distintas formas",
      text: `Puedes importar otros archivos o librerías para reutilizar código. Solidity ofrece varias sintaxis:
- Importar todo el archivo tal cual.
- Importar con un espacio de nombres (similar a 'import * as').
- Importar símbolos específicos y/o renombrarlos con 'as'.`,
      codes: [
`// formas que usaste
import "filename";
import * as symbolname from "filename";
import { symbol1 as alias, symbol2 } from "filename";`,
`// ejemplos típicos modernos
import { ERC20 } from "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "./Utils.sol" as Utils;
import * as MathLib from "./math/MathLib.sol";`
      ],
      note: `Asegúrate de que la ruta o paquete exista. Si compilas y ves “File not found”, revisa la ruta o instala la dependencia.`
    },

    {
      id: "contrato-vacio",
      heading: "3) Tu primer contrato — esqueleto",
      text: `Un contrato es un contenedor: dentro van variables, funciones, modificadores y eventos. 
Sin nada dentro, compila, pero no “hace” nada.`,
      codes: [
`// tu ejemplo base
// import solidity
pragma solidity ^0.4.0;

// import
import "filename";
import * as symbolname from "filename";
import { symbol1 as alias, symbol2 } from "filename";

// your first contract
contract SimpleContract {

}`, 
`// equivalente moderno (recomendado)
pragma solidity >=0.8.20 <0.9.0;

contract SimpleContract { }`
      ]
    },

    {
      id: "estado",
      heading: "4) Variable de estado (concepto, sin entrar a tipos aún)",
      text: `Una “variable de estado” vive en la cadena (storage). Conserva su valor entre transacciones. 
Aquí solo ilustramos la forma general (tipo + nombre). Los tipos los veremos en el capítulo siguiente.`,
      code: `// forma general (ejemplo abstracto)
Tipo nombreDeVariable;   // p.ej., uint storeData; (lo detallaremos en “Tipos”)
`
    },

    {
      id: "modifier",
      heading: "5) Modifiers — condiciones previas a ejecutar funciones",
      text: `Un 'modifier' es una condición/requisito que se evalúa antes (y/o después) de ejecutar el cuerpo de una función.
Sirve, por ejemplo, para checks de acceso, validaciones de argumentos o pausas del contrato.`,
      codes: [
`// esqueleto de un modifier
modifier onlyIf() {
  // require(condición, "mensaje de error");
  _;
  // El '_' representa el cuerpo de la función a la que se aplica
}`,
`// ejemplo con require (la lógica concreta dependerá de tus variables)
modifier nonZero(uint x) {
  require(x >= 0, "debe ser >= 0"); // demostrativo; con enteros sin signo esto siempre se cumple
  _;
}`
      ],
      note: `Evita checks inútiles: si el parámetro es 'uint' (sin signo), siempre será >= 0. Ajusta la condición a un caso real (p.ej. 'x > 0' o 'msg.sender == owner').`
    },

    {
      id: "funcion",
      heading: "6) Funciones — nombre, parámetros, visibilidad y cuerpo",
      text: `Una función recibe parámetros (opcionales), define visibilidad (public/private/internal/external) 
y ejecuta un cuerpo. Puedes aplicar modifiers para validar antes de ejecutar.`,
      code: `// ejemplo genérico
function setValor(uint x) public /* onlyIf u otro modifier */ {
  // storeData = x;  // asigna a una variable de estado (veremos tipos en el capítulo siguiente)
}`
    },

    {
      id: "eventos",
      heading: "7) Eventos — notificaciones de lo que ocurre",
      text: `Un 'event' emite un registro en los logs de la transacción. 
Son muy útiles para que las interfaces (dApps) y herramientas off-chain “escuchen” cambios.`,
      codes: [
`// declaración
event Sent(address to, uint amount);`,
`// emisión (típicamente desde una función)
emit Sent(msg.sender, 100);`
      ],
      note: `Los eventos no modifican estado; solo registran información en los logs (barato y fácil de indexar).`
    },

    {
      id: "flujo-remix",
      heading: "8) Compilar y probar en Remix (rápido)",
      list: [
        "Abre https://remix.ethereum.org, crea un nuevo archivo .sol.",
        "Escribe 'pragma', luego tus 'import' (si aplica), y por último el 'contract { ... }'.",
        "Ve al panel Solidity Compiler, elige una versión (ideal 0.8.x si usas sintaxis moderna) y compila.",
        "Ve a Deploy & Run, selecciona tu contrato y pulsa 'Deploy'.",
        "Si tu contrato no tiene funciones ni 'public' vars, la UI no mostrará botones/lecturas (es normal en un contrato vacío)."
      ]
    },

    {
      id: "compat",
      heading: "9) Nota de compatibilidad: 0.4.x vs 0.8.x",
      list: [
        "0.4.x es legado; muchas guías antiguas lo usan, pero hoy se recomienda 0.8.x.",
        "Desde 0.8.x, las operaciones aritméticas hacen 'revert' en overflow/underflow (más seguro).",
        "Sintaxis de import y buenas prácticas han madurado; usa 0.8.x salvo que mantengas contratos antiguos."
      ]
    }
  ]
};

export default SOLIDITY_BASE_SYNTAX;
